import React, { useEffect, useRef, useState } from "react";
import { PropTypes } from "prop-types";
import { generatePath, useLocation, useNavigate, useParams } from "react-router-dom";
import { bindActionCreators } from "@reduxjs/toolkit";
import { connect } from "react-redux";

//reducer
import * as actions from "./actions";

//components
import PageNotFound from "../../../../common/PageNotFound";
import Spinner from "../../../../common/Spinner";
import AppHoc from "../../../../generics/AppHOC";
import SectionHeading from "../../../../common/SectionHeading";
import ErrorComponent from "../../../../common/ErrorComponent";
import ApplicationsTableView from "../../../../views/applications/ApplicationsTableView";
import ApplicationExperienceView from "../../../../views/applications/ApplicationExperienceView/index.js";
import TimeStamp from "../../../../generics/TimeStamp";
import { getCustomListApi } from "../../../../utils/common";

//i18n
import { useI18n } from "../../../../i18n/useI18n";

//utils
import { getReportsHeader } from "../../../../../src/utils/common";

//style
import css from "../../reportsMagneticStyle.less";
import {
  shortDisplayDate24HourWithSec,
  formatDate
} from "../../../../utils/displayTime";
import { routeURLs, timeFilterTypesCustomV4 } from "../../../../utils/enums";

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(actions, dispatch)
});

const mapStateToProps = state => {
  return {
    applicationSummary: state.vanalytics.applicationSummary
  };
};

const ApplicationSummary = props => {
  const { tViews } = useI18n('views');
  const navigate = useNavigate();
  const location = useLocation();
  const { id: reportId } = useParams();
  const envVarForReports = process.env.REACT_APP_SKIP_RBAC_DISABLE_RBAC;
  const [donutViewCallback, setDonutViewCallback] = useState(false);
  const [top20ViewCallback, setTop20ViewCallback] = useState(false);
  const [qoeDistributionViewCallback, setQoeDistributionViewCallback] = useState(false);
  const [trendViewCallback, setTrendViewCallback] = useState(false);
  const [appTableViewCallback, setAppTableViewCallback] = useState(false);
  const previewRef = useRef(null);
  useEffect(() => {
    const { getReportData } = props.actions;
    getReportData(reportId);
  }, []);

  useEffect(() => {
    if (props.applicationSummary?.reportData?.data?.length > 0) {
      const reportData = props.applicationSummary.reportData.data[0];

      let globalV4PayloadReport = {
        time_frame: timeFilterTypesCustomV4[reportData.time_range],
        entry_ts: {
          start: shortDisplayDate24HourWithSec(formatDate(reportData.current_period_start).utc()),
          end: shortDisplayDate24HourWithSec(formatDate(reportData.current_period_end).utc())
        }
      };

      getCustomListApi(
        props,
        globalV4PayloadReport,
        true
      )
    }
  }, [props.applicationSummary?.reportData?.data[0]]);

    useEffect(() => {
      if (
        previewRef.current && 
        donutViewCallback && 
        top20ViewCallback && 
        qoeDistributionViewCallback && 
        trendViewCallback &&
        appTableViewCallback
      ) { 
        previewRef.current.id = "isLoaded_chart";
      }
    }, [
      props.applicationSummary,
      donutViewCallback,
      top20ViewCallback,
      qoeDistributionViewCallback,
      trendViewCallback,
      appTableViewCallback
    ]);

  // Callbacks from donut components to indicate data is available for report generation
  const isReportDonutCallback = (data) => {
    setDonutViewCallback(data); 
  }
  // Callbacks from top20 components to indicate data is available for report generation
  const isReportTop20Callback = (data) => {
    setTop20ViewCallback(data); 
  }
  // Callbacks from qoe distribution components to indicate data is available for report generation
  const isReportQoeDistributionCallback = (data) => {
    setQoeDistributionViewCallback(data); 
  }
  
  // Callbacks from trend applications widget to indicate data is available for report generation
  const isReportTrendCallback = (data) => { 
    setTrendViewCallback(data); 
  }
  // Callbacks from application table components to indicate data is available for report generation
  const isReportAppDataCallback = (data) => {
    setAppTableViewCallback(data); 
  }

  if (props.applicationSummary.reportLoader) {
    return <Spinner />;
  } else if (props.applicationSummary.errorReportData) {
    return (
      <ErrorComponent
        {...props.applicationSummary.errorMessage}
        width={"210px"}
        className={"small-dashlet-error"}
      />
    );
  } else if (props.applicationSummary.reportData.data.length > 0) {
    const reportData = props.applicationSummary.reportData.data[0];
    const {
      overlay_id,
      current_period_start,
      current_period_end,
      previous_period_start,
      previous_period_end,
      scope,
      report_uuid,
      time_range
    } = reportData;

    const applicationType = JSON.parse(scope).application[0];
    if (!(applicationType == "all") && applicationType !=='' ) {
      if (envVarForReports === "FALSE")
        if (!reportData?.is_debug) return <PageNotFound />;

      navigate(
        generatePath(routeURLs.reportApp360, { id: report_uuid, appName: applicationType }),
        { state: reportData }
      );
      return null;
    }

    const timeFilter = {
      current_period: [current_period_start, current_period_end],
      previous_period: [previous_period_start, previous_period_end]
    };
    let globalV4PayloadReport = {
      time_frame: timeFilterTypesCustomV4[time_range],
      entry_ts: {
        start: shortDisplayDate24HourWithSec(formatDate(current_period_start).utc()),
        end: shortDisplayDate24HourWithSec(formatDate(current_period_end).utc())
      }
    };

    //by default is_debug is false and report browser link is disable/block to show reports
    if (envVarForReports === "FALSE") {
      if (reportData && !reportData?.is_debug) {
        return <PageNotFound />;
      }
    }

    return (
      <div className={css["applicaiton-summary-report"]} ref={previewRef}>
        {getReportsHeader(reportData.overlay_name, location)}
        <div className="all-applicaiton-scope-section">
          <span className="details">
            <span className="title hbr-type-body3">{tViews('scopeHeading')}</span>
            <span className="value hbr-type-body3">{tViews('allApplications')}</span>
          </span>
          <TimeStamp {...props} isReport={true} reportData={reportData} />
        </div>
        <SectionHeading title={tViews('appExperience')} />
        <div className="widget-wrapper">
          <ApplicationExperienceView
            timeFilter={timeFilter}
            overlayId={overlay_id}
            reporting={true}
            isReportDonutCallback={isReportDonutCallback}
            isReportTop20Callback={isReportTop20Callback}
            isReportQoeDistributionCallback={isReportQoeDistributionCallback}
            isReportTrendCallback={isReportTrendCallback}
          /></div>
        <SectionHeading title={tViews('top50Applications')} />
        <div className="widget-wrapper">
          <ApplicationsTableView
            globalV4PayloadReport={globalV4PayloadReport}
            overlayId={overlay_id}
            reporting={true}
            isReportAppDataCallback={isReportAppDataCallback}
          /></div>
      </div>
    );
  } else {
    return <PageNotFound />;
  }
};

ApplicationSummary.propTypes = {
  applicationSummary: PropTypes.object.isRequired,
  actions: PropTypes.object
};

export default (
  connect(mapStateToProps, mapDispatchToProps)(AppHoc(ApplicationSummary))
);
